import { Map } from 'immutable';
import type { Moment } from 'moment';

import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { axiosInstance } from '@/api/axiosInstance';
import { duckAppName } from '@/constants';
import UserEntity, { type UserEntityRecord } from '@/entities/User';
import type { PaginatedResponse } from '@/types';

import { DEFAULT_AUTH_HEADER_NAME, type MockAuthMode } from './enums';
import type { MockJobData } from './MockJob';

class MockSystemEntity extends Entity {
  static paginated = true;

  static paths = {
    apiBase: '/mock_api/v1/mock_system/',
  };

  static fields: EntityFields<MockSystemEntityFields> = {
    uuid: new Fields.IdField(),
    name: new Fields.CharField(),
    slug: new Fields.CharField(),
    description: new Fields.TextField({ blank: true }),
    vendor: new Fields.CharField({ blank: true }),
    is_active: new Fields.BooleanField({ default: true }),
    auth_mode: new Fields.CharField({ default: 'api_key' }),
    auth_header_name: new Fields.CharField({
      blank: true,
      default: DEFAULT_AUTH_HEADER_NAME,
    }),
    auth_secret: new Fields.CharField({ blank: true }),
    default_headers: new Fields.AnyField({
      blank: true,
      default: () => Map(),
    }),
    default_delay_ms: new Fields.IntegerField({ default: 0 }),
    ignore_trailing_slash: new Fields.BooleanField({ default: true }),
    cors_allowed_origins: new Fields.CharField({ blank: true, default: '*' }),
    log_requests: new Fields.BooleanField({ default: true }),
    created_by: new Fields.EntityField({
      entity: UserEntity,
      blank: true,
      local: true,
    }),
    base_url: new Fields.CharField({ blank: true, local: true }),
    app_base_path: new Fields.CharField({ blank: true, local: true }),
    applications: new Fields.AnyField({ blank: true, local: true }),
    endpoint_count: new Fields.IntegerField({ blank: true, local: true }),
    can_edit: new Fields.BooleanField({ blank: true, local: true }),
    can_delete: new Fields.BooleanField({ blank: true, local: true }),
    created: new Fields.DateTimeField({ blank: true, local: true }),
    modified: new Fields.DateTimeField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<MockSystemEntityData>) {
    return super.dataToRecord(data) as MockSystemEntityRecord;
  }

  static toString(record: MockSystemEntityRecord | null | undefined) {
    return record?.get('name') ?? '';
  }

  static clearLogs(uuid: string) {
    return axiosInstance.post<{ deleted: number }>(
      `${this.paths.apiBase}${uuid}/clear_logs/`,
    );
  }

  static exportSystem(uuid: string) {
    return axiosInstance.get<MockSystemExport>(
      `${this.paths.apiBase}${uuid}/export/`,
    );
  }

  static importSystem(payload: MockSystemImportRequest) {
    return axiosInstance.post<MockSystemEntityData>(
      `${this.paths.apiBase}import/`,
      payload,
    );
  }

  static rotateSecret(uuid: string) {
    return axiosInstance.post<MockSystemEntityData>(
      `${this.paths.apiBase}${uuid}/rotate_secret/`,
    );
  }

  static enrich(uuid: string) {
    return axiosInstance.post<MockJobData>(
      `${this.paths.apiBase}${uuid}/enrich/`,
    );
  }

  static async search(
    search: string,
    { signal, pageSize = 20 }: { signal?: AbortSignal; pageSize?: number } = {},
  ) {
    const response = await axiosInstance.get<
      PaginatedResponse<MockSystemEntityData>
    >(this.paths.apiBase, {
      params: {
        ordering: 'name',
        page_size: String(pageSize),
        ...(search ? { search } : {}),
      },
      signal,
    });
    return response.data.results;
  }
}

export type MockSystemEntityFields = {
  uuid: string;
  name: string;
  slug: string;
  description: string;
  vendor: string;
  is_active: boolean;
  auth_mode: MockAuthMode;
  auth_header_name: string;
  auth_secret: string;
  default_headers: Map<string, string>;
  default_delay_ms: number;
  ignore_trailing_slash: boolean;
  cors_allowed_origins: string;
  log_requests: boolean;
  created_by: UserEntityRecord | null;
  base_url: string;
  app_base_path: string;
  applications: Map<string, unknown> | null;
  endpoint_count: number | null;
  can_edit: boolean;
  can_delete: boolean;
  created: Moment | null;
  modified: Moment | null;
};

export type MockSystemApplicationsData = {
  visible: { uuid: string; name: string; slug: string }[];
  hidden_count: number;
};

export type MockSystemEntityData = Omit<
  MockSystemEntityFields,
  'default_headers' | 'applications' | 'created_by' | 'created' | 'modified'
> & {
  default_headers: Record<string, string>;
  applications: MockSystemApplicationsData;
  created_by: Record<string, unknown> | null;
  created: string;
  modified: string;
};

export type MockSystemEntityRecord = EntityRecord<MockSystemEntityFields>;

export type MockSystemExport = {
  format: string;
  exported: string;
  system: Record<string, unknown> & { slug: string };
};

export type MockSystemImportRequest = {
  export: unknown;
  replace: boolean;
};

MockSystemEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: MockSystemEntity,
  name: 'MockSystemEntity',
});

export default MockSystemEntity;
